import React, { FC, MouseEvent, SVGProps } from 'react';
import cx from 'classnames';

import {
  SocialCircleFacebookIcon,
  SocialCircleInstagramIcon,
  SocialCircleKijijiCentralIcon,
  SocialCircleTwitterIcon,
  SocialCircleYoutubeIcon
} from '@move-ui/icons-asset-catalog';

import Clickable from '@move-ui/clickable';
import { MaskedLink } from '@move-web-essentials-utils/masked-link';
import styles from './SocialMediaIcons.css';
import {
  NavigationClickHandler,
  SocialMediaEnum,
  SocialMediaItem
} from '../../types';
import { useTranslation } from '../../utils/translations';

// TODO: Export this type from @move-ui?
type IconProps = SVGProps<SVGSVGElement> & {
  fillColor?: string;
  strokeColor?: string;
};

const configs: Record<SocialMediaEnum, { Icon: FC<IconProps>; style: string }> =
  {
    [SocialMediaEnum.Facebook]: {
      Icon: SocialCircleFacebookIcon,
      style: styles.facebook
    },
    [SocialMediaEnum.Twitter]: {
      Icon: SocialCircleTwitterIcon,
      style: styles.twitter
    },
    [SocialMediaEnum.Blog]: {
      Icon: SocialCircleKijijiCentralIcon,
      style: styles.blog
    },
    [SocialMediaEnum.YouTube]: {
      Icon: SocialCircleYoutubeIcon,
      style: styles.youtube
    },
    [SocialMediaEnum.Instagram]: {
      Icon: SocialCircleInstagramIcon,
      style: styles.instagram
    }
  };

type Props = {
  items?: SocialMediaItem[];
  onClick?: NavigationClickHandler;
  classNames?: {
    root?: string;
    item?: string;
  };
};

const SocialMediaIcons: FC<Props> = ({ items, onClick, classNames }) => {
  const { translate } = useTranslation();

  if (!items || items.length === 0) {
    return null;
  }

  const handleClick = (name: string, url: string) => (event: MouseEvent) => {
    onClick?.(name, { url }, event);
  };

  return (
    <div
      className={cx(styles.component, classNames?.root)}
      data-testid="SocialMediaIcons"
    >
      {items.map(({ name, label, url }) => {
        const { Icon, style } = configs[name];
        const localizedUrl = translate(url);

        return (
          <MaskedLink url={localizedUrl} key={name}>
            <Clickable
              aria-label={translate(label)}
              className={cx(styles.socialMediaIcon, style, classNames?.item)}
              onClick={handleClick(name, localizedUrl)}
            >
              <Icon fillColor="white" strokeColor="black" />
            </Clickable>
          </MaskedLink>
        );
      })}
    </div>
  );
};

export default SocialMediaIcons;
